// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Platform, ScrollView, StyleSheet } from 'react-native';
import { Stack, useLocalSearchParams } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';

import GoffyButton from '@/components/GoffyButton';
import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';
import { createRequestId } from '@/services/requestId';
import { isSubscriptionsFeatureEnabled } from '@/services/subscriptionFeature';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import appColors from '@/theme/colors';

type Status = {
  provider: string;
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** Stripe has a hosted billing portal to manage the subscription in; Swish does not. */
  hasHostedPortal: boolean;
  /** Which providers the server will actually sell right now. Empty means none are offerable. */
  availableProviders: string[];
};

/**
 * Maps the dotted error codes payment-service returns onto user-facing strings. Anything
 * unrecognised falls back to the caller's generic message rather than surfacing a raw code.
 */
function errorMessageKey(code: string | undefined, fallbackKey: string): string {
  switch (code) {
    case 'payments.subscriptions.disabled':
      return 'subscriptionPage.featureDisabled';
    case 'payments.provider.disabled':
    case 'payments.provider.unavailable':
    case 'payments.provider.unknown':
      return 'subscriptionPage.providerUnavailable';
    case 'payments.swish.bankNotSupported':
      return 'subscriptionPage.swishBankNotSupported';
    case 'payments.swish.alreadySubscribed':
      return 'subscriptionPage.swishAlreadySubscribed';
    case 'payments.swish.consentInProgress':
      return 'subscriptionPage.swishConsentInProgress';
    case 'payments.swish.noSubscription':
      return 'subscriptionPage.cancelNoSubscription';
    default:
      return fallbackKey;
  }
}

async function openUrl(url: string) {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
    window.location.href = url;
    return;
  }
  await WebBrowser.openBrowserAsync(url);
}

export default function SubscriptionScreen() {
  const { t, locale } = useI18n();
  const { user } = useUser();
  const params = useLocalSearchParams<{ status?: string }>();
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const socketRef = useRef(getSharedBffWebSocket(apiBaseUrl));
  const statusRequestIdRef = useRef<string | null>(null);
  const actionRequestIdRef = useRef<string | null>(null);

  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // Bumped after a cancel, which changes the subscription server-side but returns no new state.
  const [refreshKey, setRefreshKey] = useState(0);

  const { isAdmin, isDeveloper } = useEffectiveRoles(user);
  // Dark-launched for everyone else, but admin/developer reach this screen from the
  // developer menu regardless — the BFF marks them privileged server-side too (see
  // SubscriptionCheckoutStartHandler), so this is UI convenience, not the security boundary.
  const featureEnabled = isSubscriptionsFeatureEnabled() || isAdmin || isDeveloper;

  useEffect(() => {
    if (!featureEnabled || !user?.token) {
      setLoading(false);
      return;
    }

    const socket = socketRef.current;
    socket.setAuthToken(user.token);

    const requestId = createRequestId();
    statusRequestIdRef.current = requestId;
    setLoading(true);
    setError(null);
    socket.send({ type: 'subscription.status.request', requestId });

    const unsubscribe = socket.onMessage((message) => {
      const payload = message as { type?: string; requestId?: string };
      if (payload.requestId !== statusRequestIdRef.current) {
        return;
      }
      if (payload.type === 'subscription.status.ack') {
        const ack = message as {
          provider?: string;
          status?: string;
          currentPeriodEnd?: string | null;
          cancelAtPeriodEnd?: boolean;
          hasHostedPortal?: boolean;
          availableProviders?: string[];
        };
        setStatus({
          provider: ack.provider ?? '',
          status: ack.status ?? 'none',
          currentPeriodEnd: ack.currentPeriodEnd ?? null,
          cancelAtPeriodEnd: ack.cancelAtPeriodEnd ?? false,
          hasHostedPortal: ack.hasHostedPortal ?? false,
          availableProviders: ack.availableProviders ?? [],
        });
        setLoading(false);
      } else if (payload.type === 'subscription.status.error') {
        setError(t('subscriptionPage.error'));
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [featureEnabled, refreshKey, t, user?.token]);

  const handleAction = useCallback(
    (action: 'checkout' | 'portal' | 'cancel', provider?: string) => {
      const socket = socketRef.current;
      const requestId = createRequestId();
      actionRequestIdRef.current = requestId;
      setActionPending(true);
      setActionError(null);

      const base =
        action === 'checkout'
          ? 'subscription.checkout'
          : action === 'portal'
            ? 'subscription.portal'
            : 'subscription.cancel';
      const fallbackErrorKey =
        action === 'checkout'
          ? 'subscriptionPage.checkoutError'
          : action === 'portal'
            ? 'subscriptionPage.portalError'
            : 'subscriptionPage.cancelError';

      const unsubscribe = socket.onMessage((message) => {
        const payload = message as { type?: string; requestId?: string };
        if (payload.requestId !== actionRequestIdRef.current) {
          return;
        }
        if (payload.type === `${base}.ack`) {
          const ack = message as { url?: string };
          unsubscribe();
          setActionPending(false);
          if (action === 'cancel') {
            // Cancelling returns no URL — the subscription changed server-side, so re-read it.
            setRefreshKey((key) => key + 1);
          } else if (ack.url) {
            void openUrl(ack.url);
          } else {
            setActionError(t(fallbackErrorKey));
          }
        } else if (payload.type === `${base}.error`) {
          const err = message as { code?: string };
          unsubscribe();
          setActionPending(false);
          setActionError(t(errorMessageKey(err.code, fallbackErrorKey)));
        }
      });

      // 'subscription.cancel' is its own message type; checkout and portal take a '.start' suffix.
      const messageType = action === 'cancel' ? base : `${base}.start`;
      socket.send(action === 'checkout' ? { type: messageType, requestId, provider } : { type: messageType, requestId });
    },
    [t]
  );

  const renewalDate = useMemo(() => {
    if (!status?.currentPeriodEnd) return null;
    try {
      return new Date(status.currentPeriodEnd).toLocaleDateString(locale);
    } catch {
      return status.currentPeriodEnd;
    }
  }, [locale, status?.currentPeriodEnd]);

  const statusText = useMemo(() => {
    if (!status) return null;
    if (status.status === 'active' && status.cancelAtPeriodEnd && renewalDate) {
      return t('subscriptionPage.statusActiveCancelling', { date: renewalDate });
    }
    if (status.status === 'active' && renewalDate) {
      return t('subscriptionPage.statusActive', { date: renewalDate });
    }
    if (status.status === 'past_due') {
      return t('subscriptionPage.statusPastDue');
    }
    if (status.status === 'canceled') {
      return t('subscriptionPage.statusCancelled');
    }
    if (status.status === 'incomplete') {
      // Swish only: the consent exists but is unsigned, or signed and awaiting its first charge.
      return t('subscriptionPage.statusPending');
    }
    return t('subscriptionPage.statusNone');
  }, [renewalDate, status, t]);

  // past_due is still a live subscription — the user manages it rather than buying a second one.
  const isSubscribed = status?.status === 'active' || status?.status === 'past_due' || status?.status === 'incomplete';
  // Card first: it is the provider that works outside Sweden.
  const offeredProviders = useMemo(
    () => [...(status?.availableProviders ?? [])].sort((a, b) => (a === 'stripe' ? -1 : b === 'stripe' ? 1 : 0)),
    [status?.availableProviders]
  );

  return (
    <>
      <Stack.Screen options={{ title: t('subscriptionPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('subscriptionPage.title')}</Text>

        {!featureEnabled ? (
          <Text style={styles.paragraph}>{t('subscriptionPage.featureDisabled')}</Text>
        ) : !user?.token ? (
          <Text style={styles.paragraph}>{t('subscriptionPage.signInPrompt')}</Text>
        ) : (
          <>
            {params.status === 'success' ? <Text style={styles.notice}>{t('subscriptionPage.successMessage')}</Text> : null}
            {params.status === 'cancelled' ? <Text style={styles.notice}>{t('subscriptionPage.cancelledMessage')}</Text> : null}

            {loading ? (
              <View style={styles.loadingRow}>
                <ActivityIndicator size="small" />
                <Text style={styles.paragraph}>{t('subscriptionPage.loading')}</Text>
              </View>
            ) : error ? (
              <Text style={styles.errorText}>{error}</Text>
            ) : (
              <Text style={styles.paragraph}>{statusText}</Text>
            )}

            {actionError ? <Text style={styles.errorText}>{actionError}</Text> : null}

            {!loading && !error ? (
              <View style={styles.actions}>
                {isSubscribed ? (
                  status?.hasHostedPortal ? (
                    <GoffyButton
                      label={t('subscriptionPage.manageButton')}
                      onPress={() => handleAction('portal')}
                      disabled={actionPending}
                    />
                  ) : (
                    // Swish has no hosted portal to send the user to, so cancelling happens here.
                    <GoffyButton
                      label={t('subscriptionPage.cancelButton')}
                      onPress={() => handleAction('cancel')}
                      disabled={actionPending || status?.cancelAtPeriodEnd}
                    />
                  )
                ) : offeredProviders.length === 0 ? (
                  <Text style={styles.paragraph}>{t('subscriptionPage.noProviders')}</Text>
                ) : (
                  offeredProviders.map((provider) => (
                    <GoffyButton
                      key={provider}
                      label={
                        provider === 'swish'
                          ? t('subscriptionPage.upgradeWithSwishButton')
                          : t('subscriptionPage.upgradeWithCardButton')
                      }
                      onPress={() => handleAction('checkout', provider)}
                      disabled={actionPending}
                    />
                  ))
                )}
              </View>
            ) : null}
          </>
        )}
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
  },
  paragraph: {
    fontSize: 14,
    lineHeight: 20,
  },
  notice: {
    fontSize: 14,
    fontWeight: '600',
  },
  loadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  errorText: {
    fontSize: 14,
    color: appColors.status.dangerText,
  },
  actions: {
    marginTop: 8,
  },
});
